import { useNavigate, useOutletContext } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import TextAreaChat from '@/components/TextAreaChat';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { Content, Model } from '@shared/types';
import { MessageItem, MeshUploadState } from '@/types/misc';
import { cn } from '@/lib/utils';
import { SelectedItemsContext } from '@/contexts/SelectedItemsContext';
import { useSendContentMutation } from '@/services/messageService';
import { generateConversationTitle } from '@/services/conversationService';
import { Sparkles, Zap, Box } from 'lucide-react';
import { StarField } from '@/components/StarField';

export function PromptView() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isLoading } = useAuth();
  const { isSidebarOpen } = useOutletContext<{ isSidebarOpen: boolean }>();
  const queryClient = useQueryClient();

  const [model, setModel] = useState<Model>('google/gemini-3-pro-preview');
  const [isLoaded, setIsLoaded] = useState(false);
  const [images, setImages] = useState<MessageItem[]>([]);
  const [meshUpload, setMeshUpload] = useState<MeshUploadState | null>(null);

  const newConversationId = useMemo(() => {
    return crypto.randomUUID();
  }, []);

  const { mutate: sendMessage } = useSendContentMutation({
    conversation: {
      id: newConversationId,
      user_id: user?.id ?? '',
      current_message_leaf_id: null,
    },
  });

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setIsLoaded(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);



  const isAuthModalVisible = !user || user.is_anonymous;

  const { mutate: handleGenerate } = useMutation({
    mutationFn: async (content: Content) => {
      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .insert([{
          id: newConversationId,
          user_id: user?.id ?? '',
          title: 'New Conversation',
        }])
        .select()
        .single();

      if (conversationError) throw conversationError;
      sendMessage(content);

      generateConversationTitle(conversation.id, content)
        .then(async (title) => {
          await supabase
            .from('conversations')
            .update({ title })
            .eq('id', conversation.id);
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
          queryClient.invalidateQueries({ queryKey: ['conversation', conversation.id] });
        })
        .catch((error) => console.error('Failed to generate title:', error));

      return { conversationId: conversation.id, content };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      navigate(`/editor/${data.conversationId}`);
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to process prompt',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className={cn(
      'relative h-full min-h-full overflow-hidden transition-all duration-300 ease-in-out',
      isSidebarOpen && 'p-4',
    )}>
      {/* 3D Starfield background */}
      <StarField starCount={300} speed={0.3} />

      <div className={cn(
        'relative h-full min-h-full',
        isSidebarOpen && 'rounded-3xl',
      )}>
        <main className="flex h-full w-full flex-col items-center justify-center px-6 md:px-12">
          {/* Hero Section */}
          <div className="flex max-w-4xl flex-col items-center justify-center text-center">

            {/* Floating badge */}
            <div className={cn(
              'mb-6 flex items-center gap-2.5 rounded-full border border-purple-500/40 bg-gradient-to-r from-purple-500/15 via-cyan-500/10 to-pink-500/15 px-5 py-2.5 shadow-[0_0_20px_rgba(139,92,246,0.15)] transition-all duration-700',
              isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0',
            )}>
              <Sparkles className="h-4 w-4 text-purple-400" />
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">AI-Powered 3D Design</span>
            </div>

            {/* Main headline */}
            <h1 className={cn(
              'mb-4 text-4xl font-bold tracking-tight transition-all duration-700 delay-100 sm:text-5xl md:text-6xl lg:text-7xl',
              isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0',
            )}>
              <span className="bg-gradient-to-r from-dc-accent-primary via-dc-accent-secondary to-dc-accent-tertiary bg-clip-text text-transparent">
                DaddyCad
              </span>
            </h1>

            {/* Subheadline */}
            <p className={cn(
              'mb-4 max-w-2xl text-lg font-light text-dc-text-secondary transition-all duration-700 delay-200 sm:text-xl md:text-2xl',
              isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0',
            )}>
              Transform your ideas into 3D reality
            </p>

            {/* Feature pills */}
            <div className={cn(
              'mb-10 flex flex-wrap items-center justify-center gap-4 transition-all duration-700 delay-300',
              isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0',
            )}>
              <div className="group flex items-center gap-2 rounded-xl border border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-transparent px-4 py-2 backdrop-blur-sm transition-all hover:border-cyan-500/40 hover:shadow-[0_0_15px_rgba(0,212,255,0.1)]">
                <Zap className="h-4 w-4 text-cyan-400 group-hover:animate-pulse" />
                <span className="text-sm text-zinc-300">Instant Generation</span>
              </div>
              <div className="group flex items-center gap-2 rounded-xl border border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-transparent px-4 py-2 backdrop-blur-sm transition-all hover:border-purple-500/40 hover:shadow-[0_0_15px_rgba(139,92,246,0.1)]">
                <Box className="h-4 w-4 text-purple-400 group-hover:animate-pulse" />
                <span className="text-sm text-zinc-300">Export to STL/DXF</span>
              </div>
            </div>
          </div>

          {/* Input Section */}
          <div className={cn(
            'w-full max-w-3xl transition-all duration-700 delay-400',
            isAuthModalVisible && 'xl:max-w-2xl',
            isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0',
          )}>
            <SelectedItemsContext.Provider value={{ images, setImages, meshUpload, setMeshUpload }}>
              <TextAreaChat
                onSubmit={handleGenerate}
                conversation={{ id: newConversationId, user_id: user?.id ?? '' }}
                placeholder="Describe what you want to create..."
                model={model}
                setModel={setModel}
                showPromptGenerator={false}
              />
            </SelectedItemsContext.Provider>

            {/* Helper text */}
            <p className={cn(
              'mt-4 text-center text-sm text-dc-text-muted transition-all duration-700 delay-500',
              isLoaded ? 'opacity-100' : 'opacity-0',
            )}>
              Try: "Create a gear with 20 teeth" or "Design a phone stand"
            </p>
          </div>

          {isLoading && (
            <div className="absolute bottom-8 flex justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-dc-accent-primary border-t-transparent" />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
